import { Button, TextField } from "@mui/material";
import { DesktopDateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import DataContext from "../../../../../../../../../../../context/DataContext";
import { IndexedDBService } from "../../../../../../../../../../../services/indexed-db-service";
import Colors from "../../../../../../../../../../global/Colors";
import ProCliStatusDropdown from "../../../../../../../../../../global/FormComponents/FormPresets/ProCliStatus/ProCliStatusDropdown";
import STRINGS from "../../../../../CaseFollowUps/components/utils/strings";
import { setFieldData } from "../../../../../CaseFollowUps/state/actions";
import { ACTIONABLE_ITEM_TYPE_STRING } from "../../../../../types/actionTypes";
import {
  FollowUpFormDispatchContext,
  FollowUpFormStateContext,
} from "../context/FollowUpFormContext";
import { setFormIsTouched, setFormIsValid } from "../state/actions";

const Form = ({
  id,
  actionType,
  isProviderFollowUp,
  isProviderEditable,
  submitForm,
  title,
  buttonsArr,
  providerOptions,
  setGetProCliOptions,
  isFormTouched,
}) => {
  const actionItem = String(ACTIONABLE_ITEM_TYPE_STRING[actionType]).replaceAll(" ", "");
  const indexedDBService = new IndexedDBService(actionItem, "details");
  const params = useParams();
  const { userRoles } = useContext(DataContext);
  const formState = useContext(FollowUpFormStateContext);
  const formDispatch = useContext(FollowUpFormDispatchContext);
  const submitButtonDefault = "opacity-70 text-white rounded-md px-8 py-2 m-6 mb-3";
  const submitButtonActive =
    "opacity-90 hover:opacity-100 text-white rounded-md px-8 py-2 m-6 mb-3";

  const [submitButtonStyle, setSubmitButtonStyle] = useState(
    formState.isValid ? submitButtonActive : submitButtonDefault
  );
  const [selectedProvider, setSelectedProvider] = useState("");
  const [draftLoaded, setDraftLoaded] = useState();
  const [draftFollowUp, setDraftFollowUp] = useState();

  const [indexedDBKey, setIndexedDBKey] = useState();

  useEffect(() => {
    let active = true;

    if (active) {
      if (formState.isValid && formState.isTouched) {
        setSubmitButtonStyle(submitButtonActive);
      }
    }
    return () => {
      active = false;
    };
  }, [formState.isTouched, formState.isValid]);

  useEffect(() => {
    let active = true;
    if (active) {
      const indexKey = params.id + (id ? `_${id}` : `_${actionType + "_new"}`);
      setIndexedDBKey(indexKey);

      if (indexKey) {
        indexedDBService.get(indexKey).then((draft) => {
          if (draft) {
            setDraftFollowUp(draft);
            handleFieldOnChange("detail", draft);
          }
          setDraftLoaded(true);
        });
      }
    }
    return () => {
      active = false;
    };
  }, [id, params]);

  const handleDateField = (e) => {
    handleFieldOnChange("dueDate", e);
  };

  const handleDetailField = (e) => {
    handleFieldOnChange(e.target.name, e.target.value);
  };

  const handleProviderField = (value) => {
    handleFieldOnChange("provider", value);
    setSelectedProvider(value);
  };

  const handleFieldOnChange = (fname, value) => {
    if (!formState.isTouched) {
      setFormIsTouched(formDispatch, true);
    }
    if (indexedDBKey && fname === "detail") {
      indexedDBService.save(indexedDBKey, value);
    }

    setFieldData(formDispatch, {
      fieldName: fname,
      data: value,
    });

    //Bubbles up to Modal Component to clear errors upon user interaction
    isFormTouched(true);
  };

  const handleSubmitForm = (submitType) => {
    if (formState.isTouched && formState.isValid) {
      const formData = {
        action_detail: formState.fields.detail.value,
        due_date: formState.fields.dueDate.value,
        ...(formState.fields.provider && formState.fields.provider.value !== ""
          ? { on_provider: formState.fields.provider.value }
          : {}),
      };
      submitForm(submitType, formData, indexedDBKey);
    }
  };
  useEffect(() => {
    let active = true;
    if (active) {
      if (formState.fields.provider) {
        if (!formState.fields.provider.isValid) {
          setFormIsValid(formDispatch, false);
        }
        if (formState.fields.provider.isValid) {
          setFormIsValid(formDispatch, true);
        }
      }
    }
    return () => {
      active = false;
    };
  }, [formDispatch, formState.fields]);

  return (
    <div id="Form" data-testid="Form">
      <div data-testid="Dialog-Header" className=""></div>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DesktopDateTimePicker
          data-testid="field-datepicker-due-date"
          className="my-4 w-full text-center"
          label={STRINGS.FIELD_LABEL_DUE_DATE}
          id="field_followup_date"
          name="dueDate"
          value={formState.fields.dueDate.value !== "" && new Date(formState.fields.dueDate.value)}
          onChange={handleDateField}
        />
      </LocalizationProvider>
      {isProviderFollowUp && isProviderEditable && (
        <ProCliStatusDropdown
          proCliStatus={selectedProvider}
          setProCliStatusParent={handleProviderField}
          optionsList={providerOptions}
          getOptionsList={setGetProCliOptions}
        />
      )}
      {isProviderFollowUp && !isProviderEditable && (
        <div>
          <h4>Provider:</h4>
          {formState.fields.provider.displayValue}
        </div>
      )}

      <TextField
        data-testid="field-input-followup-detail"
        required={formState.fields.detail.required}
        className="my-4 w-full text-center"
        id="followup-detail"
        name="detail"
        label={STRINGS.FIELD_LABEL_REASON}
        placeholder={STRINGS.FIELD_PLACEHOLDER_REASON}
        value={formState.fields.detail.value ? formState.fields.detail.value : ""}
        variant="outlined"
        onChange={handleDetailField}
        error={formState.fields.detail.isError}
        helperText={
          formState.fields.detail.isHelperTextVisible
            ? STRINGS.FIELD_HELPERTEXT_REASON
            : STRINGS.EMPTY
        }
        multiline
        minRows={3}
        maxRows={10}
      />
      {buttonsArr.map(
        (buttonItem) =>
          userRoles.permissions.includes(buttonItem.permissions[0]) &&
          userRoles.permissions.includes(buttonItem.permissions[1]) && (
            <Button
              key={buttonItem.submitType}
              data-testid={"button-submit-" + buttonItem.submitType}
              onClick={() => handleSubmitForm(buttonItem.submitType)}
              className={submitButtonStyle}
              style={{
                backgroundColor:
                  buttonItem.submitType === "Complete-FollowUp"
                    ? Colors.green[500]
                    : Colors.blue[500],
              }}
            >
              {buttonItem.displayText}
            </Button>
          )
      )}
    </div>
  );
};
export default Form;
